import { useEffect, useState } from 'react';
import { pinGuardCheck, pinGuardRecord, verifyPin, type PinGuardState } from '@makila/pos-core';
import { Delete, LayoutDashboard, Lock } from 'lucide-react';
import type { IdbTerminalStore } from '../lib/idb-store';
import { switchTo } from '../lib/windows';

export interface Operator { id: string; name: string; role: 'cashier' | 'manager'; active: boolean; pin: { salt: string; hash: string; iterations: number } }

/**
 * Entrada do operador: escolher o nome e escrever o PIN (sem rede). As
 * tentativas falhadas ficam no dispositivo — recarregar não as apaga.
 */
export function LockScreen({ store, companyName, refreshKey, onUnlock }: { store: IdbTerminalStore; companyName: string; refreshKey?: string | null; onUnlock: (o: Operator) => void }) {
  const [ops, setOps] = useState<Operator[] | null>(null);
  const [sel, setSel] = useState<Operator | null>(null);
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void store.catalog('operator').then((items) => setOps(items.map((i) => i.data as unknown as Operator).filter((o) => o.active).sort((a, b) => a.name.localeCompare(b.name, 'pt'))));
  }, [store, refreshKey]); // relê depois de cada sincronização (operadores novos)

  const tryPin = async (p: string) => {
    if (!sel) return;
    setBusy(true);
    const key = `pinGuard:${sel.id}`;
    const g = await store.kv<PinGuardState>(key);
    const check = pinGuardCheck(g, Date.now());
    if (!check.allowed) {
      setMsg(`Demasiadas tentativas. Espere ${Math.ceil(check.waitMs / 60_000)} min ou peça a um gerente.`);
      setPin(''); setBusy(false);
      return;
    }
    const ok = await verifyPin(p, sel.pin);
    const next = pinGuardRecord(g, ok, Date.now());
    await store.setKv(key, next);
    setBusy(false);
    if (ok) { onUnlock(sel); return; }
    setPin('');
    setMsg(next.lockedUntil > Date.now() ? 'Demasiadas tentativas: este operador fica bloqueado por uns minutos.' : 'PIN errado.');
  };

  const press = (d: string) => {
    if (busy) return;
    setMsg(null);
    const p = (pin + d).slice(0, 6);
    setPin(p);
  };

  useEffect(() => {
    if (!sel) return;
    const on = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
      else if (e.key === 'Enter' && pin.length >= 4) void tryPin(pin);
      else if (e.key === 'Escape') { setSel(null); setPin(''); }
    };
    addEventListener('keydown', on);
    return () => removeEventListener('keydown', on);
  });

  if (!ops) return <div className="grid h-full place-items-center text-muted">A abrir…</div>;

  return (
    <div className="relative grid h-full place-items-center bg-ink p-4 text-white">
      <div className="w-full max-w-lg text-center">
        <img src="/makila-simbolo.svg" alt="" className="mx-auto h-12 w-12" />
        <h1 className="mt-3 font-display text-2xl font-bold">{companyName}</h1>
        {!sel && (
          <>
            <p className="mt-1 text-white/70">Quem vai vender?</p>
            {!ops.length && <p className="mt-6 rounded-xl bg-white/10 p-4">Ainda não há operadores nesta caixa. Crie-os na gestão (Operadores) e ligue a caixa à internet um momento.</p>}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {ops.map((o) => (
                <button key={o.id} onClick={() => { setSel(o); setPin(''); setMsg(null); }} className="flex h-24 flex-col items-center justify-center rounded-2xl bg-white/10 p-2 hover:bg-white/20">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-700 font-bold">{o.name.slice(0, 1).toUpperCase()}</span>
                  <span className="mt-1 line-clamp-1 font-medium">{o.name}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <p className="mt-8 text-xs text-white/40">Makila POS · versão {__POS_BUILD__}</p>
        {sel && (
          <div className="mx-auto mt-4 max-w-xs">
            <p className="text-white/80"><Lock size={16} className="mr-1 inline" />{sel.name} — escreva o PIN</p>
            <div aria-live="polite" className="my-4 flex justify-center gap-3" aria-label={`${pin.length} algarismos escritos`}>
              {Array.from({ length: 6 }, (_, i) => <span key={i} className={`h-4 w-4 rounded-full ${i < pin.length ? 'bg-white' : 'bg-white/20'}`} />)}
            </div>
            {msg && <p role="alert" className="mb-3 rounded-lg bg-brand-700 p-2 text-sm">{msg}</p>}
            <div className="grid grid-cols-3 gap-3">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <PadKey key={d} onClick={() => press(d)}>{d}</PadKey>)}
              <PadKey onClick={() => { setSel(null); setPin(''); }} small>Voltar</PadKey>
              <PadKey onClick={() => press('0')}>0</PadKey>
              <PadKey onClick={() => setPin((p) => p.slice(0, -1))} label="Apagar"><Delete /></PadKey>
            </div>
            <button disabled={pin.length < 4 || busy} onClick={() => void tryPin(pin)} className="mt-4 h-14 w-full rounded-xl bg-brand-700 text-lg font-bold disabled:opacity-40">Entrar</button>
          </div>
        )}
      </div>
      {/* Por último no documento: as teclas e os operadores vêm primeiro (leitores de ecrã e testes). */}
      <button onClick={() => switchTo('/gestao')} title="Gestão: artigos, operadores, relatórios" className="absolute right-4 top-4 flex h-12 items-center gap-2 rounded-xl px-3 text-sm font-medium text-white/60 hover:bg-white/10 hover:text-white">
        <LayoutDashboard size={18} /> Gestão
      </button>
    </div>
  );
}

function PadKey({ onClick, children, small, label }: { onClick: () => void; children: React.ReactNode; small?: boolean; label?: string }) {
  return <button aria-label={label} onClick={onClick} className={`grid h-16 place-items-center rounded-2xl bg-white/10 font-semibold hover:bg-white/20 ${small ? 'text-sm' : 'text-2xl'}`}>{children}</button>;
}
